import { AttomDataType, CustomerAddressType } from '@alpinehomeair/common';
// Deep-path import: the common barrel eagerly requires server-only deps
// (nats, jsonwebtoken) that webpack cannot bundle for the browser.
import { deriveDwellingClassification } from '@alpinehomeair/common/build/helpers/derive-dwelling-classification';
import store from 'app/store';
import { AxiosError } from 'axios';
import { LocationType } from 'data/types';
import {
  setAddressId,
  setAddressSearched,
  setAttomData,
  setIsLocationDataPending,
  setLocationData,
  setProjectSettingValue,
  setPropertyLocation,
  setPropertySummary,
  setSuggestedStructureType,
  setWeatherData
} from 'features/project';
import axios from 'utils/api';
import { getAttomSquareFootage } from 'utils/coverage-square-footage';
import { getLocationData, getWeatherData } from 'utils/data';
import { resolveDwellingSuggestion } from 'utils/dwelling-suggestion';
import { isEmpty, normalizeAddress, parseCoordinate } from 'utils/helpers';
import { getProjectSettingValue } from 'utils/project';
import { seedDefaultSizingItemInsulationValues } from 'utils/sizing';
import { errorModal, warningModal } from 'utils/ui';

export function resolvePropertyCoordinates(
  propertyLocation: LocationType | null,
  attomData: AttomDataType | null
): LocationType | null {
  if (
    propertyLocation &&
    !Number.isNaN(propertyLocation.lat) &&
    !Number.isNaN(propertyLocation.lng)
  ) {
    return propertyLocation;
  }

  const lat = parseCoordinate(attomData?.location?.latitude);
  const lng = parseCoordinate(attomData?.location?.longitude);
  if (lat !== null && lng !== null) {
    return { lat, lng };
  }

  return null;
}

// Identity token for the detached getLocationData chain below: two
// overlapping updatePropertyAttributesFromAttomData calls each start a
// chain, but isLocationDataPending is a single boolean, so only the
// NEWEST chain may dispatch location data, run the insulation seed,
// clear the pending flag, or show the failure modal. Stale chains bail.
let locationChainSeq = 0;

export async function updatePropertyAttributesFromAttomData(
  newAttomData: AttomDataType
) {
  const { dispatch } = store;
  const { structureTypes } = store.getState().data;
  const { structureUniqueId } = store.getState().structure;
  const {
    locationData,
    projectDetails,
    propertySummary,
    suggestedStructureTypeId,
    suggestedStructureSubtypeId
  } = store.getState().project;

  if (newAttomData) {
    const dataForSummary = {
      lot: newAttomData.lot,
      address: newAttomData.address,
      location: newAttomData.location,
      summary: newAttomData.summary,
      utilities: newAttomData.utilities,
      building: {
        size: newAttomData.building.size,
        rooms: newAttomData.building.rooms,
        interior: newAttomData.building.interior,
        construction: newAttomData.building.construction,
        parking: newAttomData.building.parking,
        summary: newAttomData.building.summary
      }
    };

    dispatch(
      setPropertyLocation({
        lat: Number(newAttomData.location.latitude),
        lng: Number(newAttomData.location.longitude)
      })
    );

    if (newAttomData.address.postal1) {
      // Arm the pending flag before the fetch so creation paths (the
      // autosave watcher's lazy creation, utils/data createNewStructure)
      // defer their fallback-'4' insulation seed to this chain instead of
      // persisting a possibly-wrong climate zone.
      dispatch(setIsLocationDataPending(true));
      locationChainSeq += 1;
      const chainId = locationChainSeq;
      getLocationData(newAttomData.address.postal1)
        .then(async (res) => {
          // A newer chain has taken ownership of the flag and the seed —
          // this stale response must not dispatch, seed, or clear pending.
          if (chainId !== locationChainSeq) return;
          dispatch(setLocationData(res.data ?? null));

          // Handles its own errors and the persist-vs-hydrate decision for
          // newly created structures; never rejects, so an insulation
          // failure cannot trip the location-warning catch below.
          await seedDefaultSizingItemInsulationValues(
            newAttomData,
            res.data?.climateZone ?? '4'
          );

          // Clear pending only AFTER the awaited seed: a creation landing
          // between setLocationData and seed completion still defers, and
          // the seed re-reads store.getState() after its own network GET,
          // so a flag armed mid-flight is seen and takes the persist path
          // with the real zone. A creation arming the flag in the
          // microtasks between the seed's flag read and this clear is
          // caught by the in-chain retry below.
          dispatch(setIsLocationDataPending(false));

          // A creation that deferred to this chain got zero persist
          // attempts if the seed above no-oped (empty year-built, or its
          // insulation-defaults GET failed and was swallowed): the flag is
          // still armed here. Retry once with the real zone — pending is
          // already cleared (mirroring the catch path), so a creation
          // landing during this retry's await self-seeds immediately, and
          // the retry's post-GET flag re-read then sees the flag cleared
          // and hydrates: exactly one persist-seed wins. If the retry
          // no-ops too the flag stays armed and a later ATTOM re-trigger
          // heals it (no loops). Never rejects, so it cannot trip the
          // catch below.
          if (store.getState().structure.isNewlyCreatedStructure) {
            await seedDefaultSizingItemInsulationValues(
              newAttomData,
              res.data?.climateZone ?? '4'
            );
          }
        })
        .catch(async (error: AxiosError) => {
          // Keep the console.error for observability even when stale, but a
          // stale chain must not clear pending (the newer chain owns it),
          // not fallback-seed, and not show a user-facing modal for a
          // lookup the user has already superseded.
          if (chainId !== locationChainSeq) {
            console.error('Error fetching location data (stale chain)', error);
            return;
          }

          // Clear pending FIRST so any creation from this point self-seeds
          // immediately — no stranding window.
          dispatch(setIsLocationDataPending(false));

          // A creation that deferred to this now-dead chain still needs its
          // persist-seed: give it the pre-fix fallback-'4'. When the flag is
          // not armed, do NOT seed — a plain location failure means no seed,
          // exactly as before. The seed never rejects by contract.
          if (store.getState().structure.isNewlyCreatedStructure) {
            await seedDefaultSizingItemInsulationValues(newAttomData, '4');
          }

          warningModal('Failed to load location data');
          console.error('Error fetching location data', error);
        });

      const newWeatherData = await getWeatherData(newAttomData.address.postal1);

      dispatch(setWeatherData(newWeatherData));
    } else {
      errorModal('No zip code found in attom data!');
    }

    if (
      suggestedStructureTypeId === null ||
      suggestedStructureSubtypeId === null
    ) {
      // ATTOM classifies factory-built homes deterministically — no need to
      // ask the AI for those; it stays the fallback for everything else.
      const dwellingSuggestion = resolveDwellingSuggestion(
        deriveDwellingClassification(newAttomData.summary),
        structureTypes
      );

      if (dwellingSuggestion) {
        dispatch(
          setSuggestedStructureType({
            typeId: dwellingSuggestion.typeId,
            subtypeId: dwellingSuggestion.subtypeId,
            name: dwellingSuggestion.label
          })
        );
      } else {
        const suggestStructureSubtype = await axios.post(
          'ai/determine-structure-type',
          {
            propertyData: dataForSummary,
            structureTypes: structureTypes.flatMap((t) =>
              t.subtypes.map((st) => ({
                id: st.id,
                label: st.label,
                description: st.description
              }))
            )
          }
        );
        const suggestedSubtype = suggestStructureSubtype.data;
        const suggestedSubtypeId = suggestedSubtype.replace(/\D/g, '');

        if (Number(suggestedSubtypeId)) {
          const suggestedStructureType = structureTypes.find((t) =>
            t.subtypes.some((st) => st.id === Number(suggestedSubtypeId))
          );

          if (suggestedStructureType) {
            const subtypeLabel =
              suggestedStructureType.subtypes.find(
                (st) => st.id === Number(suggestedSubtypeId)
              )?.label ?? 'Unnamed Structure';
            dispatch(
              setSuggestedStructureType({
                typeId: suggestedStructureType.id,
                subtypeId: Number(suggestedSubtypeId),
                name: subtypeLabel
              })
            );
          }
        } else {
          console.error('Failed to determine structure type');
        }
      }
    }

    if (
      newAttomData.utilities.coolingType ||
      newAttomData.utilities.heatingType
    ) {
      if (isEmpty(getProjectSettingValue('ductwork'))) {
        dispatch(setProjectSettingValue('ductwork', 'Full Ductwork'));
      }
    }

    if (newAttomData.building.construction.condition) {
      let insulationLevel = 'Good';

      if (newAttomData.building.construction.condition === 'POOR') {
        insulationLevel = 'Poor';
      } else if (newAttomData.building.construction.condition === 'AVERAGE') {
        insulationLevel = 'Good';
      } else if (newAttomData.building.construction.condition === 'GOOD') {
        insulationLevel = 'Great';
      }

      if (isEmpty(getProjectSettingValue('insulation_level'))) {
        dispatch(setProjectSettingValue('insulation_level', insulationLevel));
      }
    }

    const attomSquareFootage = getAttomSquareFootage(newAttomData);
    if (
      attomSquareFootage !== null &&
      isEmpty(getProjectSettingValue('square_footage'))
    ) {
      dispatch(setProjectSettingValue('square_footage', attomSquareFootage));
    }

    if (newAttomData.building.summary.levels) {
      if (isEmpty(getProjectSettingValue('number_of_floors'))) {
        dispatch(
          setProjectSettingValue(
            'number_of_floors',
            newAttomData.building.summary.levels
          )
        );
      }
    }

    if (newAttomData.building.rooms.beds) {
      if (isEmpty(getProjectSettingValue('number_of_bedrooms'))) {
        dispatch(
          setProjectSettingValue(
            'number_of_bedrooms',
            newAttomData.building.rooms.beds
          )
        );
      }
    }

    if (newAttomData.building.rooms.bathsTotal) {
      if (isEmpty(getProjectSettingValue('number_of_bathrooms'))) {
        dispatch(
          setProjectSettingValue(
            'number_of_bathrooms',
            newAttomData.building.rooms.bathsTotal
          )
        );
      }
    }

    if (newAttomData.building.interior.fplcCount) {
      if (isEmpty(getProjectSettingValue('number_of_fireplaces'))) {
        dispatch(
          setProjectSettingValue(
            'number_of_fireplaces',
            newAttomData.building.interior.fplcCount
          )
        );
      }
    }

    if (newAttomData.building.interior.bsmtSize) {
      if (isEmpty(getProjectSettingValue('foundation_type'))) {
        dispatch(setProjectSettingValue('foundation_type', 'Basement'));
      }
    }

    const { yearBuilt } = newAttomData.summary;

    if (yearBuilt) {
      if (isEmpty(getProjectSettingValue('year_built'))) {
        dispatch(setProjectSettingValue('year_built', yearBuilt));
      }
    }

    if (!propertySummary) {
      dispatch(setPropertySummary('Loading...'));

      const summarizeProperty = await axios.post('ai/summarize-property', {
        propertyData: JSON.stringify(dataForSummary)
      });

      dispatch(
        setPropertySummary(
          summarizeProperty.data ?? 'Failed to generate property summary'
        )
      );
    }

    if (
      newAttomData.address.line1 &&
      newAttomData.address.locality &&
      newAttomData.address.countrySubd &&
      newAttomData.address.postal1 &&
      newAttomData.address.country
    ) {
      const validLine2 =
        newAttomData.address.line2 !==
        `${newAttomData.address.locality}, ${newAttomData.address.countrySubd} ${newAttomData.address.postal1}`;
      const addressRequest = await axios.post('address', {
        address1: newAttomData.address.line1,
        address2: validLine2 ? newAttomData.address.line2 : null,
        city: newAttomData.address.locality,
        state: newAttomData.address.countrySubd,
        zip: newAttomData.address.postal1,
        country: newAttomData.address.country,
        customerId: projectDetails?.customerId
      });
      const address = addressRequest.data as CustomerAddressType | null;

      if (address) {
        dispatch(setAddressId(address.id));

        if (structureUniqueId) {
          await axios.patch('/structure-address-link', {
            structureUniqueId,
            addressId: address.id
          });
        }
      }
    }
  } else {
    dispatch(setPropertyLocation(null));
    dispatch(setPropertySummary(null));

    if (!locationData && projectDetails?.postal) {
      getLocationData(projectDetails.postal)
        .then((res) => dispatch(setLocationData(res.data ?? null)))
        .catch((error: AxiosError) => {
          warningModal('Failed to load location data');
          console.error('Error fetching location data', error);
        });
    }

    if (locationData?.zipcode || projectDetails?.postal) {
      const newZipCode = locationData?.zipcode || projectDetails?.postal;

      if (newZipCode) {
        const newWeatherData = await getWeatherData(newZipCode);
        dispatch(setWeatherData(newWeatherData));
      }
    }
  }
}

export async function lookupAddress(address: string) {
  try {
    const propertyDataRequest = await axios.get('property-data', {
      params: {
        address: normalizeAddress(address)
      }
    });
    const newPropertyData = propertyDataRequest.data as AttomDataType;

    if (newPropertyData) {
      store.dispatch(setAttomData(newPropertyData));
      updatePropertyAttributesFromAttomData(newPropertyData);
    } else {
      errorModal(
        `No property data found for ${address}. Please confirm the address is correct.`
      );
    }
  } catch (error) {
    console.error(error);
    errorModal('Error fetching property data');
  } finally {
    store.dispatch(setAddressSearched(true));
  }
}
